import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "motion/react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, Photo } from "../../../kit.jsx";
import { peopleMet } from "../../../data/content.js";
import { LECTERN } from "./album.js";
import { ArrowLink } from "../shared.jsx";

export function PeopleMet() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="ab-met" id="met" aria-labelledby="ab-met-title">
      <header className="ab-met__head">
        <MaskWords id="ab-met-title" className="ab-h2" text="People we met." />
        <p className="ab-body">
          {peopleMet.length} conversations and visits, each with its own page. <strong>A meeting is not an endorsement</strong> and not a partnership.
        </p>
      </header>
      <ul className="ab-met__list">
        {peopleMet.map((meeting, i) => (
          <motion.li
            key={meeting.slug}
            initial={reduceMotion ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: i * 0.06, ease: EASE_OUT }}
          >
            <Link to={`/meetings/${meeting.slug}`}>
              <span className="ab-met__img">
                <Photo src={meeting.image} alt={`${meeting.name}, meeting record`} sizes="(max-width: 767px) 50vw, 20vw" />
                <span className="ab-met__tag">Meeting</span>
              </span>
              <small>{meeting.date}</small>
              <strong>{meeting.name}</strong>
              <span>{meeting.role}</span>
              <ArrowUpRight weight="bold" aria-hidden="true" />
            </Link>
          </motion.li>
        ))}
      </ul>
    </section>
  );
}

// Months on the strip, December 2025 to September 2026 (today).
const MONTHS = ["Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Now"];

const EVENTS = [
  { month: 0, row: "top", date: "Dec 2025", title: "The bootcamp spark, then GRID starts", src: "/images/grid-photo-06-p7.jpg", alt: "Co-founders Moksh Sindhwani and Tanishq Aggarwal" },
  { month: 1, row: "bottom", date: "Jan 2026", title: "Aurora launches", src: "/images/grid-photo-10-p8.jpg", alt: "Aurora overall winner Jerovin Floyd with his laptop" },
  { month: 5, row: "top", date: "May 2026", title: "Metamorphosis outreach", src: "/images/metamorphosis/album/meta-538-changemaker-certificates-stage-800.webp", alt: "Metamorphosis students holding up their certificates on stage at IIT Delhi" },
  { month: 7, row: "bottom", date: "Jul 2026", title: "Meetings in Delhi", src: "/images/people-met/DamvNL9yFYV.jpg", alt: "GRID team members with Ajai Chowdhry" },
  { month: 8, row: "top", date: "1–2 Aug 2026", title: "AAROH at IIIT Delhi", src: LECTERN.srcSmall ?? LECTERN.src, alt: LECTERN.alt },
];

export function TimelineStrip() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const play = reduceMotion || inView;
  return (
    <section className="ab-strip" id="timeline" aria-labelledby="ab-strip-title">
      <header className="ab-strip__head">
        <MaskWords id="ab-strip-title" className="ab-h2" text="Nine months, in order." />
        <ArrowLink to="/journey">Read the full story</ArrowLink>
      </header>
      <div className="ab-strip__track" ref={ref}>
        <ol className="ab-strip__events">
          {EVENTS.map((event, i) => (
            <motion.li
              key={event.date}
              className={`is-${event.row}`}
              style={{ "--col": event.month + 1 }}
              initial={reduceMotion ? false : { opacity: 0, y: event.row === "top" ? 18 : -18 }}
              animate={play ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.7, delay: 0.4 + i * 0.14, ease: EASE_OUT }}
            >
              <span className="ab-strip__img"><Photo src={event.src} alt={event.alt} sizes="(max-width: 767px) 40vw, 18vw" /></span>
              <small>{event.date}</small>
              <strong>{event.title}</strong>
            </motion.li>
          ))}
        </ol>
        <div className="ab-strip__axis" aria-hidden="true">
          <motion.span
            className="ab-strip__line"
            initial={reduceMotion ? false : { scaleX: 0 }}
            animate={play ? { scaleX: 1 } : undefined}
            transition={{ duration: 1.4, ease: [0.65, 0, 0.35, 1] }}
          />
          {MONTHS.map((month, i) => (
            <span key={month} className={`ab-strip__tick ${EVENTS.some((e) => e.month === i) ? "has-event" : ""}`} style={{ "--col": i + 1 }}>
              {month}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
